import {
  Autocomplete,
  MenuItem,
  TextFieldProps,
  TextField as TextFieldMui,
} from '@mui/material';
import { Field, useField, useFormikContext } from 'formik';
import { TextField } from 'formik-mui';
import { every, filter, find, isNil, isString, map, sortBy } from 'lodash';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';
import { formatUtils, formUtils } from 'utils';

export type FormikSelectOption = {
  label: React.ReactNode | string;
  value: string;
  disabled?: boolean;
  // Fully-rendered extra content shown next to the label (e.g. status chips) — the caller
  // decides what this looks like, FormikSelect just renders it.
  children?: React.ReactNode;
};

const AUTOCOMPLETE_MIN_OPTIONS = 10;
const AUTOCOMPLETE_LISTBOX_MAX_HEIGHT = 216;
const AUTOCOMPLETE_LISTBOX_SCROLLBAR_SX = {
  maxHeight: AUTOCOMPLETE_LISTBOX_MAX_HEIGHT,
  overflowY: 'scroll',
  pr: 0.5,
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0, 0, 0, 0.38) transparent',
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundClip: 'content-box',
    backgroundColor: 'rgba(0, 0, 0, 0.38)',
    border: '2px solid transparent',
    borderRadius: 8,
  },
};

type Props = {
  label: React.ReactNode | string;
  name: string;
  options: FormikSelectOption[];
  noneOption?: boolean;
  showMetaError?: boolean;
  autocomplete?: boolean;
  onChange?: (value: string) => void;
  disablePortal?: boolean;
  enableIdSearch?: boolean;
  optionDisabledText?: string;
} & Omit<Partial<TextFieldProps>, 'onChange'>;

export type { Props as FormikSelectProps };

export const FormikSelect: React.FC<Props> = ({
  label,
  name,
  options,
  noneOption,
  showMetaError,
  autocomplete = true,
  disablePortal = true,
  enableIdSearch = true,
  optionDisabledText,
  ...rest
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.formik_select',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const formik = useFormikContext();

  const [field, meta, helpers] = useField(name);
  const selectedOption = useMemo(
    () => find(options, (option) => option.value === field.value),
    [field.value, options],
  );
  const [prevSelectedOption, setPrevSelectedOption] = useState(selectedOption);
  const [inputValue, setInputValue] = useState(
    selectedOption ? String(selectedOption.label) : '',
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filterOptions = useMemo(() => {
    if (!inputValue?.trim() || !enableIdSearch) {
      return undefined;
    }

    const searchQuery = formatUtils.formatSearchString(inputValue.trim());
    if (!formUtils.isUUID(searchQuery)) {
      return undefined;
    }

    return (options: FormikSelectOption[]) =>
      filter(
        options,
        (o) => formatUtils.formatSearchString(o.value) === searchQuery,
      );
  }, [enableIdSearch, inputValue]);

  const isSelectedDisabled = useMemo(
    () => find(options, { value: field.value })?.disabled,
    [field.value, options],
  );

  const noneOptionConfig = useMemo(
    () => ({
      value: '',
      label: t('none_option'),
    }),
    [t],
  );

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key: any },
      option: FormikSelectOption,
    ) => {
      const { key, ...optionProps } = props;
      return (
        <li key={key} {...optionProps}>
          {option.label}
          {option.children}
        </li>
      );
    },
    [],
  );

  const asAutocomplete = useMemo(
    () =>
      autocomplete &&
      !rest?.disabled &&
      every(options, (option) => isString(option.label)) &&
      options.length >= AUTOCOMPLETE_MIN_OPTIONS,
    [autocomplete, options, rest?.disabled],
  );

  const autocompleteOptions = useMemo(() => {
    // mirror existing sorting logic for plain select (< 10 options)
    let result = sortBy(options, 'disabled');
    if (noneOption) {
      result = [noneOptionConfig, ...result];
    }
    return result;
  }, [options, noneOption, noneOptionConfig]);

  const handleAutocompleteChange = useCallback(
    (value: string) => {
      helpers.setValue(value);
      helpers.setValue(value, true);
      if (!meta.touched) {
        setTimeout(() => {
          helpers.setTouched(true);
          formik.validateForm();
        }, 0);
      }
      rest.onChange?.(value);
    },
    [helpers, rest, meta, formik],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      formik.handleChange(e);
      rest.onChange?.(e.target.value);
    },
    [formik, rest],
  );

  useEffect(() => {
    if (asAutocomplete && !isNil(field.value)) {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  }, [asAutocomplete, field.value]);

  useEffect(() => {
    // handle providing selectedOption later e.g. due to loading options and filtering
    if (asAutocomplete && field?.value) {
      const optionLabel = selectedOption ? String(selectedOption?.label) : '';
      setInputValue(optionLabel);
    }
  }, [asAutocomplete, field?.value, selectedOption]);

  if (asAutocomplete) {
    return (
      <Autocomplete
        options={autocompleteOptions}
        getOptionLabel={(option) =>
          option.value === '' ? noneOptionConfig.label : String(option.label)
        }
        disablePortal={disablePortal}
        filterOptions={filterOptions}
        getOptionDisabled={(option) => !!option.disabled}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextFieldMui
            {...params}
            {...rest}
            label={label}
            size="small"
            inputRef={inputRef}
            value={inputValue}
            {...(isSelectedDisabled && {
              error: true,
              helperText: optionDisabledText || tCommon('errors.invalid'),
            })}
            {...(meta.error &&
              (meta.touched || showMetaError) && {
                error: true,
                helperText: meta.error,
              })}
            onBlur={() => {
              if (isNil(field.value) && inputValue) {
                setInputValue(
                  prevSelectedOption?.label
                    ? String(prevSelectedOption?.label)
                    : '',
                );
                handleAutocompleteChange(
                  String(prevSelectedOption?.value || ''),
                );
              } else if (
                !isNil(field.value) &&
                inputValue !== String(selectedOption?.label ?? '')
              ) {
                setInputValue(
                  selectedOption ? String(selectedOption.label) : '',
                );
              }
            }}
            onChange={(e) => {
              if (!isNil(field.value)) {
                setPrevSelectedOption(selectedOption);
              }
              setInputValue(e.target.value);
            }}
          />
        )}
        value={
          find(autocompleteOptions, { value: field.value }) ||
          (null as any as FormikSelectOption)
        }
        inputValue={inputValue}
        disableClearable
        fullWidth
        noOptionsText={t('no_options')}
        getOptionKey={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        slotProps={{
          paper: {
            sx: {
              '& .MuiAutocomplete-listbox': AUTOCOMPLETE_LISTBOX_SCROLLBAR_SX,
            },
          },
        }}
        onChange={(_, newValue, reason) => {
          if (newValue && newValue.value === '') {
            handleAutocompleteChange('');
            setInputValue('');
          } else {
            handleAutocompleteChange(newValue ? String(newValue.value) : '');
            setInputValue(newValue ? String(newValue.label) : '');
          }
        }}
      />
    );
  }

  return (
    <Field
      {...rest}
      {...(rest.onChange && { onChange: handleChange })}
      component={TextField}
      name={name}
      label={label}
      select
      size="small"
      {...(isSelectedDisabled && {
        error: true,
        helperText: optionDisabledText || tCommon('errors.invalid'),
      })}
      {...(showMetaError &&
        meta.error && {
          error: true,
          helperText: meta.error,
        })}
    >
      {noneOption && (
        <MenuItem value={noneOptionConfig.value}>
          {noneOptionConfig.label}
        </MenuItem>
      )}
      {map(sortBy(options, 'disabled'), (option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
          {option.children}
        </MenuItem>
      ))}
    </Field>
  );
};
